import { NotificationController } from 'core-components/library/popup'
import { isHPUrl } from 'src/core/layout/login/helpers/isHPUrl'
import { IBaseLocationState, IRouteData, TRoutesAccess } from 'src/core/routes/types'

interface ICalculateRedirectionDataParams {
  hash?: string
  state?: IBaseLocationState
  pathname: string
  loginResult?: boolean
}

export const calculateRedirectData = ({
  hash,
  state,
  pathname,
  loginResult,
}: ICalculateRedirectionDataParams) => {
  const redirectHash = hash?.includes('#redirectPage') ? hash.slice('#redirectPage='.length) : null
  const fromStateUrl = state?.from
  const redirectUrl = redirectHash || fromStateUrl || pathname
  const isManualRedirect = typeof loginResult === 'boolean'

  return { redirectUrl, isManualRedirect }
}

// A same-origin URL built from a site-relative path, or null. Rejects absolute URLs, '//host',
// 'javascript:' and anything with a backslash or a control character. The URL parser drops tabs
// and newlines, so '/\t/evil.com' would otherwise become '//evil.com'.
export const toSameOriginUrl = (value: unknown): URL | null => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return null
  }
  // eslint-disable-next-line no-control-regex
  if (/[\\\x00-\x1f\x7f]/.test(value)) {
    return null
  }
  try {
    const url = new URL(value, window.location.origin)
    // Dot segments are resolved while parsing: '/..//evil.com' becomes the path '//evil.com',
    // which a browser reads as another host once it is written back as a relative URL
    if (url.origin !== window.location.origin || url.pathname.startsWith('//')) {
      return null
    }
    return url
  } catch {
    return null
  }
}

interface IAuthRedirectOptions {
  // Only for a URL our own identity service returned. Never for a value taken from a link.
  allowHPUrl?: boolean
}

export const processAuthRedirect = (
  redirectUrl: string | null,
  { allowHPUrl = false }: IAuthRedirectOptions = {}
): boolean => {
  if (redirectUrl) {
    const inner = toSameOriginUrl(redirectUrl)
    const target = inner ? inner.href : allowHPUrl && isHPUrl(redirectUrl) ? redirectUrl : null

    if (target) {
      window.location.replace(target)
      return true
    }
    NotificationController.showError({
      message:
        'The redirect URL is incorrect. Contact your system administrator or try again later',
    })
  }
  return false
}

export const getCustomRoute = (
  customRoutes: IRouteData[],
  access: TRoutesAccess[] = [],
  pathname: string
) => {
  return customRoutes
    .filter((route) => (route.access ? access.includes(route.access) : true))
    .find((route) => {
      const path = route.path.replaceAll('*', '')
      const [mainPath] = path.split(':')
      return pathname.includes(mainPath)
    })
}

export const redirect = (url: string) => {
  // Same-origin paths only. Absolute URLs were already ignored here; now '//host' and
  // 'javascript:' are ignored too.
  const target = toSameOriginUrl(url)
  if (target) {
    window.location.replace(target.href)
  }
}
